import {RefObject} from 'preact/compat';
import {useCallback, useEffect, useMemo, useRef, useState} from 'preact/hooks';

/**
 *
 * @see https://hidde.blog/using-javascript-to-trap-focus-in-an-element/
 * @return ref, refCallback
 */
export function useTabFocusTrap(): [
  RefObject<HTMLElement | null>,
  () => RefObject<HTMLDivElement>,
] {
  const focusTrapRef = useRef<HTMLElement | null>(null);

  const [firstFocusableEl, setFirstFocusableEl] = useState<
    null | (Element & {focus?: () => void})
  >(null);
  const [lastFocusableEl, setLastFocusableEl] = useState<
    null | (Element & {focus?: () => void})
  >(null);

  const setFocusTrap = useCallback((node: HTMLElement) => {
    focusTrapRef.current = node;

    if (focusTrapRef.current) {
      const focusableEls = focusTrapRef.current?.querySelectorAll(
        `a[href]:not([disabled]),
               button:not([disabled]),
               div[role="button"][tabindex="0"],
               textarea:not([disabled]),
               input[type="text"]:not([disabled]),
               input[type="radio"]:not([disabled]),
               input[type="checkbox"]:not([disabled]),
               select:not([disabled])`,
      ) ?? [null, null];

      setFirstFocusableEl(focusableEls[0]);
      setLastFocusableEl(focusableEls[focusableEls.length - 1]);
    }
  }, []);

  const tabTrapHandler = useCallback(
    (event: KeyboardEvent) => {
      const tabKeyCode = 9;
      const activeElement = document.activeElement?.shadowRoot
        ? document.activeElement.shadowRoot.activeElement
        : document.activeElement;

      if (event.key === 'Tab' || event.keyCode === tabKeyCode) {
        if (event.shiftKey) {
          /* shift + tab */
          if (activeElement === firstFocusableEl) {
            lastFocusableEl?.focus?.();
            event.preventDefault();
          }
        } /* tab */ else if (activeElement === lastFocusableEl) {
          firstFocusableEl?.focus?.();
          event.preventDefault();
        }
      }
    },
    [firstFocusableEl, lastFocusableEl],
  );

  useEffect(() => {
    if (focusTrapRef.current) {
      focusTrapRef.current.addEventListener('keydown', tabTrapHandler);
    }
    return () =>
      focusTrapRef.current?.removeEventListener('keydown', tabTrapHandler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusTrapRef.current, tabTrapHandler]);

  return useMemo(
    () => [focusTrapRef, setFocusTrap as () => RefObject<HTMLDivElement>],
    [focusTrapRef, setFocusTrap],
  );
}
